import React, {
	ElementType,
	ReactElement,
	ReactNode,
	SyntheticEvent,
	cloneElement,
	isValidElement,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { matchPath } from 'react-router';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
	Badge,
	Box,
	Tab as MUITab,
	Tabs as MUITabs,
	TabsProps as MUITabsProps,
	Paper,
	SxProps,
	Theme,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import * as Styled from './styled';

export interface ITab {
	label: ReactNode;
	icon?: ReactNode | ElementType;
	value?: string;
	matchValue?: string;
	href?: string;
	element?: ReactNode;
	end?: boolean;
	badge?: number;
	disabled?: boolean;
	comingSoon?: boolean;
	comingSoonText?: string;
}

interface TabsProps extends Omit<MUITabsProps, 'onChange'> {
	tabs: ITab[];
	isRoute?: boolean;
	isWrap?: boolean;
	value?: number;
	onChange?: (value: number) => void;
	useValueInOnChange?: boolean;
	children?: ReactNode;
	preventUnmount?: boolean;
	header?: ReactNode;
	tabsWrapperStyles?: SxProps<Theme>;
	tabStyles?: SxProps<Theme>;
	customTab?: {
		tab: ITab;
		tabContent: ReactNode;
	};
	forceRemountOnTabChange?: boolean;
}

function Tabs({
	tabs,
	isRoute,
	isWrap = false,
	value = 0,
	onChange,
	useValueInOnChange = false,
	children,
	preventUnmount = false,
	header,
	tabsWrapperStyles = {},
	customTab,
	forceRemountOnTabChange = false,
	tabStyles,
	...props
}: TabsProps) {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const tabsRef = useRef<HTMLDivElement | null>(null);
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const customTabIndex = -1;

	const routeTabIndex = isRoute
		? useMemo(
				() =>
					tabs?.findIndex((tab) =>
						matchPath({ path: tab.matchValue || tab.value || '', end: tab.end }, pathname),
					),
				[tabs, pathname],
			)
		: null;

	const [searchParams] = useSearchParams();
	const openTabParam = parseInt(searchParams.get('openTab') || '', 10);

	const initialTabIndex =
		!Number.isNaN(openTabParam) && openTabParam >= 0 && openTabParam < tabs.length
			? openTabParam
			: (value ?? 0);

	const [stateTabIndex, setTabIndex] = useState<number>(initialTabIndex);

	const isCustomTabSelected = stateTabIndex === customTabIndex;
	const tabIndex = isRoute ? routeTabIndex : stateTabIndex;
	const currentTab = isCustomTabSelected
		? customTab?.tab
		: tabs?.[isRoute ? (routeTabIndex ?? 0) : stateTabIndex];

	const handleChangeTab = (_: SyntheticEvent, newValue: number) => {
		if (searchParams.has('openTab')) {
			const newParams = new URLSearchParams(searchParams);
			newParams.delete('openTab');
			navigate({ pathname, search: newParams.toString() }, { replace: true });
		}

		if (newValue === customTabIndex) {
			setTabIndex(customTabIndex);
			return;
		}

		const selectedTab = tabs[newValue];

		if (isRoute) {
			const path = selectedTab?.value;
			if (path) {
				navigate(path);
			}
		} else {
			setTabIndex(newValue);
		}

		const emittedValue = useValueInOnChange ? (selectedTab?.value ?? newValue) : newValue;
		onChange?.(+emittedValue);
	};

	const renderTabIcon = (icon?: ITab['icon']) => {
		if (!icon) return null;

		if (isValidElement(icon)) {
			return icon;
		}

		if (typeof icon === 'function') {
			const IconComponent = icon as ElementType;
			return <IconComponent />;
		}

		return icon;
	};

	const renderTabLabel = (tab: ITab) => (
		<Box
			component="span"
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '8px',
			}}
		>
			{tab.icon ? <Box component="span">{renderTabIcon(tab.icon)}</Box> : null}
			<Box component="span">{tab.label}</Box>
			{!!tab.badge && <Badge>{tab.badge}</Badge>}
		</Box>
	);

	const renderTab = (tab: ITab, key: number) => (
		<MUITab
			key={key}
			disableRipple={tab.disabled}
			sx={{ cursor: tab.disabled ? 'not-allowed' : 'pointer' }}
			label={
				tab.comingSoon ? (
					<Styled.ToolTip
						title={<Typography variant="body2">{tab.comingSoonText || 'Coming Soon'}</Typography>}
						arrow
						enterTouchDelay={0}
					>
						<Box sx={{ color: 'text.disabled' }}>{renderTabLabel(tab)}</Box>
					</Styled.ToolTip>
				) : (
					renderTabLabel(tab)
				)
			}
			value={!tab.comingSoon && key}
			disabled={tab.disabled}
		/>
	);

	const renderTabContent = (tab?: ITab, key?: number) => {
		if (!tab) return null;

		const isCurrent = currentTab === tab;
		const rawContent = tab?.element || children;

		const content = isValidElement(rawContent)
			? cloneElement(rawContent as ReactElement<any>, { tabIndex })
			: rawContent;

		if (!content) {
			return null;
		}

		const WrapperComponent: any = isWrap || preventUnmount ? Paper : Box;
		const baseWrapStyles: SxProps<Theme> = {
			mb: '16px',
			p: '16px',
			position: 'relative',
			display: isCurrent ? 'block' : 'none',
		};

		const basePlainStyles: SxProps<Theme> = {
			display: isCurrent ? 'block' : 'none',
		};

		// eslint-disable-next-line no-nested-ternary
		const normalizedTabStyles = tabStyles
			? Array.isArray(tabStyles)
				? tabStyles
				: [tabStyles]
			: [];

		const wrapperStyles: SxProps<Theme> =
			isWrap || preventUnmount
				? [baseWrapStyles, ...normalizedTabStyles]
				: [basePlainStyles, ...normalizedTabStyles];

		const contentKey = forceRemountOnTabChange ? (key ?? tabIndex) : key;

		return isWrap || preventUnmount ? (
			<WrapperComponent key={contentKey} sx={wrapperStyles}>
				{content}
			</WrapperComponent>
		) : (
			<Box key={contentKey} sx={wrapperStyles}>
				{content}
			</Box>
		);
	};

	const renderTabsContent = () => {
		if (preventUnmount) {
			return (
				<>
					{tabs.map(renderTabContent)}
					{customTab && renderTabContent(customTab.tab, customTabIndex)}
				</>
			);
		}

		if (isCustomTabSelected && customTab) {
			const contentKey = forceRemountOnTabChange ? `custom-${tabIndex}` : 'custom';
			return <Box key={contentKey}>{customTab.tabContent}</Box>;
		}

		return renderTabContent(currentTab);
	};

	useEffect(() => {
		if (!isMobile) {
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return;
		}

		const scroller = tabsRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
		if (!scroller) return;

		scrollerRef.current = scroller;

		const updateScrollState = () => {
			const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
			setCanScrollLeft(scroller.scrollLeft > 0);
			setCanScrollRight(scroller.scrollLeft < maxScrollLeft - 1);
		};

		updateScrollState();
		scroller.addEventListener('scroll', updateScrollState);
		window.addEventListener('resize', updateScrollState);

		let resizeObserver: ResizeObserver | null = null;
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(updateScrollState);
			resizeObserver.observe(scroller);
			const flexContainer = scroller.querySelector('.MuiTabs-flexContainer');
			if (flexContainer) {
				resizeObserver.observe(flexContainer);
			}
		}

		return () => {
			scroller.removeEventListener('scroll', updateScrollState);
			window.removeEventListener('resize', updateScrollState);
			resizeObserver?.disconnect();
		};
	}, [isMobile, tabs?.length, customTab]);

	const scrollBy = (direction: 'left' | 'right') => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const delta = direction === 'left' ? -200 : 200;
		scroller.scrollBy({ left: delta, behavior: 'smooth' });
	};

	return (
		<>
			<Styled.TabsWrap sx={tabsWrapperStyles}>
				<Styled.TabsScrollerWrap>
					<MUITabs
						{...props}
						ref={tabsRef}
						value={isCustomTabSelected ? customTabIndex : tabIndex}
						onChange={handleChangeTab}
						variant={isMobile ? 'scrollable' : props.variant}
						scrollButtons={isMobile ? false : props.scrollButtons}
					>
						{tabs?.map(renderTab)}
						{customTab && (
							<MUITab
								key="custom"
								label={renderTabLabel(customTab.tab)}
								value={customTabIndex}
								disabled={customTab.tab.disabled}
							/>
						)}
					</MUITabs>
					{isMobile && canScrollLeft && (
						<Styled.ScrollFade direction="left">
							<Styled.ScrollArrowButton
								size="small"
								aria-label="Scroll tabs left"
								onClick={() => scrollBy('left')}
							>
								<ChevronLeftIcon fontSize="small" />
							</Styled.ScrollArrowButton>
						</Styled.ScrollFade>
					)}
					{isMobile && canScrollRight && (
						<Styled.ScrollFade direction="right">
							<Styled.ScrollArrowButton
								size="small"
								aria-label="Scroll tabs right"
								onClick={() => scrollBy('right')}
							>
								<ChevronRightIcon fontSize="small" />
							</Styled.ScrollArrowButton>
						</Styled.ScrollFade>
					)}
				</Styled.TabsScrollerWrap>
				{header}
			</Styled.TabsWrap>

			{renderTabsContent()}
		</>
	);
}

export default Tabs;
